import React from 'react';
import Icon from '../../../components/AppIcon';

const ComparisonDashboard = ({ comparison, currentScan, onViewPreviousScan }) => {
    if (!comparison) return null;

    const {
        scores,
        skills,
        skillGapClosure,
        improvementScore,
        improvementLevel,
        scanDates
    } = comparison;

    // Color coding based on improvement level
    const getLevelColor = (level) => {
        switch (level) {
            case 'excellent': return 'text-green-600 bg-green-50 border-green-200';
            case 'good': return 'text-blue-600 bg-blue-50 border-blue-200';
            case 'slight': return 'text-cyan-600 bg-cyan-50 border-cyan-200';
            case 'minimal': return 'text-gray-600 bg-gray-50 border-gray-200';
            case 'declined': return 'text-red-600 bg-red-50 border-red-200';
            default: return 'text-gray-600 bg-gray-50 border-gray-200';
        }
    };

    const getDeltaColor = (delta) => {
        if (Math.abs(delta) < 5) return 'text-gray-500'; // Neutral for small changes
        if (delta > 0) return 'text-green-600';
        if (delta < 0) return 'text-red-600';
        return 'text-gray-600';
    };

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const renderDelta = (delta, percentChange) => {
        const isStable = Math.abs(delta) < 5;

        if (isStable) {
            return (
                <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm font-medium text-gray-500 bg-gray-100">
                    <Icon name="Activity" size={16} />
                    <span>Score Stable</span>
                </div>
            );
        }

        return (
            <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm font-semibold ${getDeltaColor(delta)}`}>
                <Icon
                    name={delta >= 0 ? "TrendingUp" : "TrendingDown"}
                    size={16}
                />
                <span>{delta > 0 ? '+' : ''}{delta} points</span>
                {percentChange !== 0 && (
                    <span className="text-xs opacity-70">({percentChange > 0 ? '+' : ''}{percentChange}%)</span>
                )}
            </div>
        );
    };

    return (
        <div className="bg-gradient-to-br from-white to-gray-50 border-2 border-primary/20 rounded-xl p-6 shadow-lg">
            {/* Header with Trophy */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                        <Icon name="TrendingUp" size={28} className="text-primary" />
                        Progress Comparison
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                        Comparing with scan from {formatDate(scanDates.previous)} ({scanDates.daysBetween} days ago)
                    </p>
                </div>

                {/* Overall Improvement Badge */}
                <div className={`flex items-center gap-2 px-4 py-3 rounded-lg border-2 ${getLevelColor(improvementLevel.level)}`}>
                    <span className="text-2xl">{improvementLevel.emoji}</span>
                    <div>
                        <div className="text-xs font-medium opacity-70">Overall Improvement</div>
                        <div className="text-lg font-bold">{improvementLevel.label}</div>
                        <div className="text-xs font-semibold">{improvementScore > 0 ? '+' : ''}{improvementScore} points</div>
                    </div>
                </div>
            </div>

            {/* Score Comparison Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {/* Overall Score */}
                <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="font-semibold text-gray-700">Overall Resume Score</h3>
                        <Icon name="FileText" size={20} className="text-gray-400" />
                    </div>

                    <div className="flex items-end gap-4 mb-2">
                        <div className="text-3xl font-bold text-gray-400">{scores.overall.previous}%</div>
                        <Icon name="ArrowRight" size={20} className="text-gray-400 mb-2" />
                        <div className="text-3xl font-bold text-primary">{scores.overall.current}%</div>
                    </div>

                    {renderDelta(scores.overall.delta, scores.overall.percentChange)}

                    {/* Progress Bar */}
                    <div className="mt-3 bg-gray-200 rounded-full h-2 overflow-hidden">
                        <div
                            className="bg-primary h-full transition-all duration-500 rounded-full"
                            style={{ width: `${scores.overall.current}%` }}
                        />
                    </div>
                </div>

                {/* Job Match Score */}
                <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="font-semibold text-gray-700">Job Match Score</h3>
                        <Icon name="Target" size={20} className="text-gray-400" />
                    </div>

                    <div className="flex items-end gap-4 mb-2">
                        <div className="text-3xl font-bold text-gray-400">{scores.jobMatch.previous}%</div>
                        <Icon name="ArrowRight" size={20} className="text-gray-400 mb-2" />
                        <div className="text-3xl font-bold text-primary">{scores.jobMatch.current}%</div>
                    </div>

                    {renderDelta(scores.jobMatch.delta, scores.jobMatch.percentChange)}

                    {/* Progress Bar */}
                    <div className="mt-3 bg-gray-200 rounded-full h-2 overflow-hidden">
                        <div
                            className="bg-primary h-full transition-all duration-500 rounded-full"
                            style={{ width: `${scores.jobMatch.current}%` }}
                        />
                    </div>
                </div>
            </div>

            {/* Skills Changes */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {/* New Skills */}
                {skills.new.length > 0 && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="bg-green-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">
                                +{skills.new.length}
                            </div>
                            <h4 className="font-semibold text-green-800">New Skills Added</h4>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {skills.new.slice(0, 5).map((skill, idx) => (
                                <span
                                    key={idx}
                                    className="inline-block px-2 py-1 bg-white text-green-700 text-xs font-medium rounded border border-green-300"
                                >
                                    ✨ {skill}
                                </span>
                            ))}
                            {skills.new.length > 5 && (
                                <span className="text-xs text-green-600 font-medium">+{skills.new.length - 5} more</span>
                            )}
                        </div>
                    </div>
                )}

                {/* Skill Gaps Closed */}
                {skillGapClosure.addressed.length > 0 && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">
                                {skillGapClosure.addressed.length}
                            </div>
                            <h4 className="font-semibold text-blue-800">Skill Gaps Closed</h4>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {skillGapClosure.addressed.slice(0, 5).map((skill, idx) => (
                                <span
                                    key={idx}
                                    className="inline-block px-2 py-1 bg-white text-blue-700 text-xs font-medium rounded border border-blue-300"
                                >
                                    ⭐ {skill}
                                </span>
                            ))}
                            {skillGapClosure.addressed.length > 5 && (
                                <span className="text-xs text-blue-600 font-medium">+{skillGapClosure.addressed.length - 5} more</span>
                            )}
                        </div>
                    </div>
                )}

                {/* Lost Skills (if any) */}
                {skills.lost.length > 0 && (
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 group relative">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="bg-orange-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">
                                {skills.lost.length}
                            </div>
                            <div className="flex items-center gap-1">
                                <h4 className="font-semibold text-orange-800">Not Detected</h4>
                                <Icon name="HelpCircle" size={14} className="text-orange-600 cursor-help" />
                            </div>

                            {/* Tooltip */}
                            <div className="absolute top-0 left-0 w-full h-full bg-white/95 backdrop-blur-sm border border-orange-200 rounded-lg p-4 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none flex items-center justify-center text-center z-10 shadow-lg">
                                <p className="text-xs text-orange-800 font-medium">
                                    These skills were found previously but weren't explicitly detected in this run. If you didn't remove them, the AI might have just missed them this time.
                                </p>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {skills.lost.slice(0, 5).map((skill, idx) => (
                                <span
                                    key={idx}
                                    className="inline-block px-2 py-1 bg-white text-orange-700 text-xs font-medium rounded border border-orange-300"
                                >
                                    {skill}
                                </span>
                            ))}
                            {skills.lost.length > 5 && (
                                <span className="text-xs text-orange-600 font-medium">+{skills.lost.length - 5} more</span>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Summary Stats */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                    <div>
                        <div className="text-2xl font-bold text-gray-900">{skills.totalCurrent}</div>
                        <div className="text-xs text-gray-600">Total Skills Now</div>
                    </div>
                    <div>
                        <div className="text-2xl font-bold text-gray-900">{skills.retained}</div>
                        <div className="text-xs text-gray-600">Skills Retained</div>
                    </div>
                    <div>
                        <div className="text-2xl font-bold text-gray-900">{skillGapClosure.remainingGaps}</div>
                        <div className="text-xs text-gray-600">Skill Gaps Remaining</div>
                    </div>
                    <div>
                        <div className="text-2xl font-bold text-gray-900">{scanDates.daysBetween}</div>
                        <div className="text-xs text-gray-600">Days Between Scans</div>
                    </div>
                </div>
            </div>

            {/* View Previous Scan Link */}
            {onViewPreviousScan && (
                <div className="mt-4 text-center">
                    <button
                        onClick={onViewPreviousScan}
                        className="text-sm text-primary hover:text-primary/80 font-medium inline-flex items-center gap-1"
                    >
                        <Icon name="ChevronLeft" size={16} />
                        View Previous Scan
                    </button>
                </div>
            )}
        </div>
    );
};

export default ComparisonDashboard;
